import {
	HttpClient,
	HttpHeaders,
	HttpParams,
	HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
	BehaviorSubject,
	Observable,
	catchError,
	map,
	of,
	throwError,
} from 'rxjs';
import { AccountService } from 'src/app/pages/account/services/account.service';
import { environment } from 'src/environments/environment';

@Injectable({
	providedIn: 'root',
})
export class CustomDomainService {
	API_URL = environment.fundraiser_url;
	isCustomHomePage: boolean = false;
	isFundAsCustomHomePage: boolean = false;

	constructor(
		public httpClient: HttpClient,
		public accountService: AccountService
	) {}

	// BehaviorSubject to hold the state of the custom domain
	public isCustomDomain: BehaviorSubject<boolean> =
		new BehaviorSubject<boolean>(false);

	// Getter for the custom domain state
	getIsCustomDomain() {
		return this.isCustomDomain.asObservable();
	}

	// Setter for the custom domain state
	setIsCustomDomainValue(value: boolean) {
		this.isCustomDomain.next(value);
	}

	// Method to check if the custom domain is unique
	isCustomDomainUnique(customDomain: string) {
		const url = 'custom-domain/isCustomDomainUnique';

		// Use HttpParams for the GET request parameters
		const params = new HttpParams().set('custom_domain', customDomain);

		// The GET request should not have a body, use params instead
		return this.httpClient.get(this.API_URL + url, { params });
	}

	/**
	 * This function is used to add a custom domain.
	 * @param {string} customDomain - The custom domain to be added.
	 * @returns {Observable} - Returns an Observable from the HttpClient POST request.
	 */
	addCustomDomain(customDomain: string): Observable<any> {
		const url = 'custom-domain/addCustomDomain';
		// Use HttpParams for the POST request parameters
		const body = { custom_domain: customDomain };
		let headers = this.accountService.getHeaders();

		// Return the HttpClient POST request Observable
		return this.httpClient
			.post(this.API_URL + url, body, { headers: headers })
			.pipe(
				// Catch and handle any errors that occur during the request
				catchError(this.handleError('addCustomDomain', []))
			);
	}

	/**
	 * This function is used to get the custom domain.
	 * It sends a GET request to the 'custom-domain/getCustomDomain' endpoint.
	 * @returns {Observable} - Returns an Observable that you can subscribe to.
	 */
	getCustomDomain(): Observable<any> {
		const url = 'custom-domain/getCustomDomain';
		let headers = this.accountService.getHeaders();

		// Use a try-catch block for error handling
		try {
			// The GET request should not have a body, use params instead
			return this.httpClient.get(this.API_URL + url, { headers: headers });
		} catch (error) {
			// Handle any errors here
			console.error('An error occurred:', error);
			return throwError(error); // rethrow the error to the caller
		}
	}

	/**
	 * This function is used to get the custom domain.
	 * It sends a GET request to the 'custom-domain/getCustomDomain' endpoint.
	 * @returns {Observable} - Returns an Observable that you can subscribe to.
	 */
	async getCustomDomainData(domain: string) {
		//domain = 'alam.whydonate.net';

		try {
			// GET USER ID FIRST USING DOMAIN
			const API_URL = environment.CUSTOM_DOMAIN_API_V2;
			const URL = 'custom_domain/verification';

			const res: any = await this.httpClient
				.get(API_URL + URL, {
					params: { domain },
				})
				.toPromise();

			const user_id = res?.data?.id || 0;

			if (user_id != 0) {
				localStorage.setItem('user_id_custom_domain', user_id);
			}

			// EXTRACT DATA NOW
			const url = 'custom-domain/data';
			const headers = this.accountService.getHeaders();
			const params = {
				user_id: user_id,
				language: this.accountService.getLocaleId(),
			};

			try {
				const customDomainData = await this.httpClient
					.get(this.API_URL + url, {
						headers,
						params,
					})
					.toPromise();

				// Process customDomainData as needed
				return customDomainData;
			} catch (error) {
				console.error(
					'An error occurred while fetching custom domain data:',
					error
				);
				return {};
				// Handle error appropriately
			}
		} catch (error) {
			console.error('An error occurred while verifying custom domain:', error);
			// Handle error appropriately
			return {};
		}
	}

	/**
	 * This function is used to get the DNS info for custom domain.
	 * @returns {Observable} - Returns an Observable that you can subscribe to.
	 */
	getDNSInfo(): Observable<any> {
		const url = 'custom-domain/getDNSInfo';
		let headers = this.accountService.getHeaders();

		// Use a try-catch block for error handling
		try {
			// The GET request should not have a body, use params instead
			return this.httpClient.get(this.API_URL + url, { headers: headers });
		} catch (error) {
			// Handle any errors here
			console.error('An error occurred:', error);
			return throwError(error); // rethrow the error to the caller
		}
	}

	deleteCustomDomain() {
		const url = 'custom-domain/deleteCustomDomain';
		let headers = this.accountService.getHeaders();

		// Use a try-catch block for error handling
		try {
			return this.httpClient.delete(this.API_URL + url, { headers: headers });
		} catch (error) {
			// Handle any errors here
			console.error('An error occurred:', error);
			return throwError(error); // rethrow the error to the caller
		}
	}

	/**
	 * This function is used to set custom domain settings.
	 * @param dataPayload - The data payload to be sent in the POST request.
	 */
	setCustomDomainSettings(dataPayload: any): Observable<any> {
		// Define the URL endpoint for the POST request
		const url = 'custom-domain/setCustomDomainSettings';

		// Get the headers for the POST request from the account service
		let headers = this.accountService.getHeaders();

		// Return the HttpClient POST request Observable
		return this.httpClient
			.post(this.API_URL + url, dataPayload, { headers: headers })
			.pipe(
				// Catch and handle any errors that occur during the request
				catchError(this.handleError('setCustomDomainSettings', []))
			);
	}

	/**
	 * This function is used to set custom domain settings.
	 * @param dataPayload - The data payload to be sent in the POST request.
	 */
	setCustomDomainBackground(image: any): Observable<any> {
		// Define the URL endpoint for the POST request
		const url = 'custom-domain/background';

		// Get the headers for the POST request from the account service
		let headers = this.accountService.getHeaders();
		let jwt = headers?.get('Authorization') || '';
		let options = {
			headers: new HttpHeaders().set('Authorization', jwt),
		};

		const body = new FormData();
		body.append('image', image);

		// Return the HttpClient POST request Observable
		return this.httpClient.post(this.API_URL + url, body, options).pipe(
			// Catch and handle any errors that occur during the request
			catchError(this.handleError('setCustomDomainBackground', []))
		);
	}

	/**
	 * This function is used to get custom domain settings.
	 */
	getCustomDomainSettings(): Observable<any> {
		// Define the URL endpoint for the POST request
		const url = 'custom-domain/getCustomDomainSettings';

		// Get the headers for the POST request from the account service
		let headers = this.accountService.getHeaders();

		// Return the HttpClient POST request Observable
		return this.httpClient
			.get(this.API_URL + url, {
				headers: headers,
				params: {
					language: this.accountService.getLocaleId() || 'nl',
				},
			})
			.pipe(
				// Catch and handle any errors that occur during the request
				catchError(this.handleError('getCustomDomainSettings', []))
			);
	}

	/**
	 * This function makes a GET request to the provided URL and returns true if the response status is 200 or 201, and false otherwise.
	 * @param url - The URL to which the GET request will be sent
	 * @returns An Observable that emits true if the response status is 200 or 201, and false otherwise
	 */
	checkUrlStatus() {
		let api_endpoint = 'custom-domain/status';
		let headers = this.accountService.getHeaders();
		return this.httpClient.get(this.API_URL + api_endpoint, {
			headers: headers,
		});
	}

	/**
	 * Handle Http operation that failed.
	 * Let the app continue.
	 * @param operation - name of the operation that failed
	 * @param result - optional value to return as the observable result
	 */
	private handleError<T>(operation = 'operation', result?: T) {
		return (error: any): Observable<T> => {
			// TODO: send the error to remote logging infrastructure
			console.log(error); // log to console instead

			// Let the app keep running by returning an empty result.
			return of(result as T);
		};
	}

	setIsCustomHomeFlag(value: boolean) {
		this.isCustomHomePage = value;
	}

	setIsFundAsCustomHomePage(value: boolean) {
		this.isFundAsCustomHomePage = value;
	}

	getIsCustomHomeFlag() {
		return this.isCustomHomePage;
	}

	getIsFundAsCustomHomePageFlag() {
		return this.isFundAsCustomHomePage;
	}
}
